import {
  PipelineConfigPayload,
  PipelineSettingsFormFields,
  PipelineSettingsFormState
} from './interfaces/pipeline-settings-form-state';
import {
  Integration,
  PipelineConfig,
  PipelineDestinationTableSettings,
  PipelineLoadMode,
  PipelineReplicationTypeMode,
  PipelineSchemaEvolution,
  PipelineTransactionalFailureAction
} from '../../shared/interfaces/integration';
import { PIPELINE_SETTINGS_FORM_INITIAL_VALUES } from './constants/pipeline-settings';
import {
  PipelineSettingsFeatures,
  PipelineSettingsFeaturesData,
  SyncFrequencyTypes,
  SyncType
} from '../interfaces/pipeline-settings-config';
import { getSyncFrequencyTypeFromFeatures } from '../PipelineSyncFrequency/SyncFrequencyWithCronFormField/utils';

export const PipelineSettingsConfigHelper = {
  getInitialValues(
    features: PipelineSettingsFeaturesData,
    pipeline: Integration,
    defaultPipelineName?: string
  ): PipelineSettingsFormState {
    return {
      ...PIPELINE_SETTINGS_FORM_INITIAL_VALUES,
      [PipelineSettingsFormFields.PIPELINE_NAME]: pipeline.name || defaultPipelineName || '',
      [PipelineSettingsFormFields.DESTINATION_PREFIX]: pipeline.destinationPrefix || '',
      [PipelineSettingsFormFields.DESTINATION_PARTITION_KEY]:
        (pipeline.metadata && (pipeline.metadata as Record<string, string>).partition_key) ||
        features.features.partition_key?.default_value ||
        '',
      [PipelineSettingsFormFields.LOAD_MODE]: pipeline.config.load_mode || PipelineLoadMode.APPEND,
      [PipelineSettingsFormFields.ERROR_MODE]:
        pipeline.config.batch_transactional_failure || PipelineTransactionalFailureAction.PARTIAL,
      [PipelineSettingsFormFields.OBJECT_LEVEL_ERROR_MODE]:
        pipeline.config.object_transactional_failure ||
        PipelineTransactionalFailureAction.ALL_OR_NONE,
      [PipelineSettingsFormFields.SCHEMA_EVOLUTION]:
        pipeline.config.schema_evolution || PipelineSchemaEvolution.ALLOW_ALL,
      [PipelineSettingsFormFields.SYNC_FREQUENCY]:
        pipeline.config.sync_frequency_in_ms ||
        features.features.sync_frequency_in_ms?.default_value,
      [PipelineSettingsFormFields.DESTINATION_TABLE_SETTINGS]:
        pipeline.config.destination_table_settings ||
        features.features.destination_table_settings?.default_value ||
        PipelineDestinationTableSettings.DROP_AND_LOAD,
      [PipelineSettingsFormFields.REPLICATION_TYPE]:
        pipeline.config.replication_type ||
        features.features.replication_type?.default_value ||
        PipelineReplicationTypeMode.HISTORICAL_AND_INCREMENTAL,
      [PipelineSettingsFormFields.LATENCY_THRESHOLD_ALERT_ENABLED]:
        !!pipeline.config.latency_threshold_alert_enabled,
      [PipelineSettingsFormFields.LATENCY_THRESHOLD]: pipeline.config.latency_threshold_in_minutes,
      [PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON]:
        pipeline.config.sync_frequency_in_cron || '',
      //  choose right optionz from features like hours or minutes using the syncfrequency_in_ms
      ...(features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_TYPE]
        ? {
            [PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE]: getSyncFrequencyTypeFromFeatures(
              features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_TYPE].options,
              pipeline.config.sync_frequency_in_ms ||
                features.features.sync_frequency_in_ms?.default_value ||
                0,
              pipeline.config.sync_frequency_type ||
                features.features.sync_frequency_type?.default_value
            )
          }
        : {}),
      ...(features.features[PipelineSettingsFeatures.SYNC_TYPE]
        ? {
            [PipelineSettingsFormFields.SYNC_TYPE]:
              pipeline.config.sync_type || features.features.sync_type?.default_value
          }
        : {})
    };
  },

  buildConfigParams(
    formData: PipelineSettingsFormState,
    pipeline?: Integration
  ): PipelineConfigPayload {
    let newConfig: PipelineConfig = {
      ...(pipeline ? { ...pipeline.config } : {}),
      load_mode: formData[PipelineSettingsFormFields.LOAD_MODE],
      schema_evolution: formData[PipelineSettingsFormFields.SCHEMA_EVOLUTION],
      batch_transactional_failure: formData[PipelineSettingsFormFields.ERROR_MODE],
      object_transactional_failure: formData[PipelineSettingsFormFields.OBJECT_LEVEL_ERROR_MODE],
      sync_frequency_in_ms: formData[PipelineSettingsFormFields.SYNC_FREQUENCY],
      partition_key: formData[PipelineSettingsFormFields.DESTINATION_PARTITION_KEY],
      destination_table_settings: formData[PipelineSettingsFormFields.DESTINATION_TABLE_SETTINGS],
      replication_type: formData[PipelineSettingsFormFields.REPLICATION_TYPE],
      sync_type: formData[PipelineSettingsFormFields.SYNC_TYPE],
      latency_threshold_alert_enabled:
        formData[PipelineSettingsFormFields.LATENCY_THRESHOLD_ALERT_ENABLED],
      latency_threshold_in_minutes: formData[
        PipelineSettingsFormFields.LATENCY_THRESHOLD_ALERT_ENABLED
      ]
        ? formData[PipelineSettingsFormFields.LATENCY_THRESHOLD]
        : undefined
    };

    const syncFrequencyTypeField = formData[PipelineSettingsFormFields.SYNC_FREQUENCY_TYPE];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const sync_frequency_type =
      syncFrequencyTypeField && 'value' in syncFrequencyTypeField
        ? syncFrequencyTypeField.value
        : undefined;

    const syncTypeField = formData[PipelineSettingsFormFields.SYNC_TYPE];

    if (sync_frequency_type === SyncFrequencyTypes.FIXED) {
      newConfig = { ...newConfig, sync_frequency_type, sync_frequency_in_cron: undefined };
    }

    if (sync_frequency_type === SyncFrequencyTypes.CRON) {
      newConfig = {
        ...newConfig,
        sync_frequency_type,
        sync_frequency_in_cron: formData[PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON],
        sync_frequency_in_ms: undefined
      };
    }

    if (syncTypeField === SyncType.ON_DEMAND) {
      newConfig = {
        ...newConfig,
        sync_frequency_in_ms: undefined,
        sync_frequency_type: undefined
      };
    }

    return {
      name: formData[PipelineSettingsFormFields.PIPELINE_NAME],
      destination_prefix: formData[PipelineSettingsFormFields.DESTINATION_PREFIX],
      config: {
        ...newConfig
      }
    };
  }
};
